import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { GlassIconButton } from 'dashboard/components/common';
import { API } from 'utilities/api';
import TemplatePreview from './TemplatePreview';
import { slugify } from './utils';

/**
 * Create-page dialog. The title field auto-fills the slug until the
 * user types in the slug box themselves.
 *
 * Title is a combo box, not a plain text field: most pages are one of a
 * dozen well-known names, and picking "Contact" beats typing it. It stays
 * free-text (`freeSolo`) because the moment it does not, someone needs a page
 * the list has never heard of and the dialog becomes a wall. Titles that get
 * published join the list for next time, so the suggestions grow into the
 * names people actually use instead of the ones we guessed.
 */
export default function NewPageModal({ open, onClose, onCreate, isSubmitting, templates, existingSlugs = [], brandName }) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  /**
   * Full width to start with.
   *
   * A section already decides its own inner width — hero centres at 860,
   * features spread into a grid, a text block stops at 760 — so the page's
   * width is a second constraint on top of that, and at 'default' it caps
   * everything at 1200 whatever the section wanted. The sections that look
   * wrong boxed are the ones people reach for first: a full-bleed hero, a
   * picture band, a pricing row. Starting edge to edge lets the section's own
   * judgement stand, and narrowing is one choice away in Page settings.
   */
  const [layout, setLayout] = useState('fullwidth');
  const [templateKey, setTemplateKey] = useState('blank');
  const [titleOptions, setTitleOptions] = useState([]);
  const [tplQuery, setTplQuery] = useState('');
  const [tplCategory, setTplCategory] = useState('All');
  // On by default: a page nobody can navigate to is the common mistake this
  // asks about, not a preference. Someone building a landing page they will
  // link to from an ad is the exception, and they have a checkbox.
  const [addToMenu, setAddToMenu] = useState(true);

  // Checked here as well as in the database. The unique index is the real
  // guard, but finding out after pressing Create — from an error naming a
  // Mongo index — is a bad way to learn that /about is taken.
  const slugTaken = existingSlugs
    .map((x) => String(x).toLowerCase())
    .includes((slug || slugify(title)).toLowerCase());

  useEffect(() => {
    if (open) {
      setTitle(''); setSlug(''); setLayout('default'); setTemplateKey('blank');
    }
  }, [open]);

  // Fetched per opening rather than once on mount: a title published from
  // another tab (or by another brand) should show up without a reload, and
  // the list is small enough that re-reading it costs nothing.
  useEffect(() => {
    if (!open) return;
    API.listSitePageTitles?.()
      .then((res) => setTitleOptions(Array.isArray(res) ? res : []))
      .catch(() => setTitleOptions([])); // typing still works without them
  }, [open]);

  // Shared by typing and by picking from the list, so a chosen suggestion
  // fills the slug exactly as typing it would.
  const applyTitle = (v) => {
    setTitle(v);
    setSlug((curr) => (curr ? curr : slugify(v)));
  };

  const allTemplates = (templates && templates.length > 0)
    ? templates
    : [{ key: 'blank', name: 'Blank', description: 'Start empty', section_count: 0, category: 'Page', tags: [] }];

  const categories = ['All', ...Array.from(new Set(allTemplates.map((t) => t.category || 'Page')))];

  // Matches name, description and tags. Tags carry the words people actually
  // search for — "barber" finds the salon template, which is named nothing
  // like it.
  const q = tplQuery.trim().toLowerCase();
  const tplList = allTemplates.filter((t) => {
    if (tplCategory !== 'All' && (t.category || 'Page') !== tplCategory) return false;
    if (!q) return true;
    return [t.name, t.description, ...(t.tags || [])]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        New page
        <GlassIconButton label='Close'
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size='small'
        >
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Autocomplete
          freeSolo
          fullWidth
          options={titleOptions}
          value={title}
          // Fires when an option is picked; `onInputChange` covers typing.
          onChange={(_e, v) => applyTitle(typeof v === 'string' ? v : '')}
          onInputChange={(_e, v, reason) => {
            // 'reset' fires when the dialog clears the value itself. Letting
            // it through would wipe the slug the moment the field blurs.
            if (reason !== 'reset') applyTitle(v);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size='small'
              required
              label='Title'
              placeholder='Home'
              helperText='Pick one or type your own.'
            />
          )}
          sx={{ mb: '14px' }}
        />
        <TextField
          size='small'
          label='Slug (URL path)'
          value={slug}
          onChange={(e) => setSlug(slugify(e.target.value))}
          placeholder='home'
          fullWidth
          error={slugTaken}
          helperText={slugTaken
            ? `You already have a page at /${slug || slugify(title)}. Pick a different address.`
            : `The page will live at /${slug || 'home'} on your brand site.`}
          sx={{ mb: '14px' }}
        />
        <FormControl size='small' fullWidth sx={{ mb: '14px' }}>
          <InputLabel>Layout</InputLabel>
          <Select
            label='Layout'
            value={layout}
            onChange={(e) => setLayout(e.target.value)}
          >
            <MenuItem value='default'>Default</MenuItem>
            <MenuItem value='fullwidth'>Full width</MenuItem>
            <MenuItem value='sidebar'>Sidebar</MenuItem>
            <MenuItem value='custom'>Custom</MenuItem>
          </Select>
        </FormControl>
        <Typography variant='body2' fontWeight='600' mb='6px'>
          Start from a template
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing='8px' mb='12px'>
          <TextField
            size='small'
            placeholder='Search templates — try "shop", "barber", "portfolio"'
            value={tplQuery}
            onChange={(e) => setTplQuery(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon fontSize='small' sx={{ mr: '6px', color: 'text.disabled' }} /> }}
            sx={{ flex: 1 }}
          />
          <FormControl size='small' sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select label='Category' value={tplCategory} onChange={(e) => setTplCategory(e.target.value)}>
              {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>

        {tplList.length === 0 && (
          <Typography variant='body2' color='text.secondary' fontStyle='italic' mb='12px'>
            Nothing matches “{tplQuery}”. Try a different word, or start from Blank.
          </Typography>
        )}
        <Grid container spacing='8px'>
          {tplList.map((t) => {
            const active = templateKey === t.key;
            return (
              <Grid item xs={12} md={6} key={t.key}>
                <Box
                  role='button'
                  onClick={() => setTemplateKey(t.key)}
                  sx={{
                    p: '12px',
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: active ? 'primary.main' : 'divider',
                    bgcolor: active ? 'action.selected' : 'transparent',
                    cursor: 'pointer',
                    transition: 'border-color 120ms ease, background-color 120ms ease',
                  }}
                >
                  <TemplatePreview preview={t.preview} brandName={brandName} />
                  <Typography fontWeight='700' variant='body2' mt='8px'>{t.name}</Typography>
                  {t.description && (
                    <Typography variant='caption' color='text.secondary' mt='2px' display='block'>
                      {t.description}
                    </Typography>
                  )}
                  {Number(t.section_count) > 0 && (
                    <Typography variant='caption' color='text.secondary' mt='4px' display='block'>
                      {t.section_count} section{t.section_count === 1 ? '' : 's'}
                    </Typography>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '14px 20px', flexWrap: 'wrap', gap: '8px', justifyContent: 'space-between' }}>
        <FormControlLabel
          control={
            <Checkbox
              size='small'
              checked={addToMenu}
              onChange={(e) => setAddToMenu(e.target.checked)}
            />
          }
          label={
            <Typography variant='body2'>
              Put it in the site menu
            </Typography>
          }
        />
        <Box>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant='contained'
          onClick={() => onCreate({
            title,
            slug: slug || slugify(title),
            layout,
            template_key: templateKey,
            addToMenu,
          })}
          disabled={!title || slugTaken || isSubmitting}
        >
          {isSubmitting ? 'Creating…' : 'Create'}
        </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
